import { useEffect, useState } from 'react'
import { useControls, useTransformContext } from 'react-zoom-pan-pinch'
import { TrayType } from 'components/organisms/EditTrayModal/EditTrayModal.types'

// Types
export interface TrayMapWithControlsProps {
  isInEditMode: boolean
  isLoadingWithUDIs: boolean
  trayType: TrayType
  trayImg?: string
  isLegendOpen: boolean
  setIsLegendOpen: (open: boolean) => void
}

export type PanDirection = 'up' | 'down' | 'left' | 'right'

// Constants
const MOBILE_BREAKPOINT = 768
const PAN_STEP = 130
const ZOOM_STEP = 0.5

// Custom hook for tray map controls logic
export const useTrayMapControls = () => {
  const { zoomIn, zoomOut, setTransform, resetTransform } = useControls()
  const transformContext = useTransformContext()
  const [areControlsVisible, setAreControlsVisible] = useState<boolean>(false)
  const [isMobile, setIsMobile] = useState<boolean>(false)

  // Initialize mobile detection and reset transform on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT)
    }

    checkMobile()
    resetTransform()

    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [resetTransform])

  // Pan handling logic
  const handlePan = (direction: PanDirection): void => {
    if (!transformContext.mounted) return

    const {
      positionX,
      positionY,
      previousScale: scale,
    } = transformContext.getContext().state

    const newPosition = calculateNewPosition(
      direction,
      positionX,
      positionY,
      scale
    )
    setTransform(newPosition.x, newPosition.y, newPosition.scale)
  }

  // Zoom handling logic
  const handleZoomIn = (): void => {
    zoomIn(ZOOM_STEP)
  }

  const handleZoomOut = (): void => {
    zoomOut(ZOOM_STEP)
  }

  // Controls visibility logic
  const toggleControlsVisibility = (): void => {
    setAreControlsVisible(!areControlsVisible)
  }

  return {
    // State
    areControlsVisible,
    isMobile,

    // Actions
    handlePan,
    handleZoomIn,
    handleZoomOut,
    toggleControlsVisibility,
  }
}

// Utility functions
const calculateNewPosition = (
  direction: PanDirection,
  currentX: number,
  currentY: number,
  scale: number
): { x: number; y: number; scale: number } => {
  switch (direction) {
    case 'up':
      return { x: currentX, y: currentY - PAN_STEP, scale }
    case 'down':
      return { x: currentX, y: currentY + PAN_STEP, scale }
    case 'left':
      return { x: currentX - PAN_STEP, y: currentY, scale }
    case 'right':
      return { x: currentX + PAN_STEP, y: currentY, scale }
    default:
      return { x: currentX, y: currentY, scale }
  }
}

// Legend handling logic
export const useLegendControls = (
  isLegendOpen: boolean,
  setIsLegendOpen: (open: boolean) => void
) => {
  const toggleLegend = (): void => {
    setIsLegendOpen(!isLegendOpen)
  }

  const openLegend = (): void => {
    setIsLegendOpen(true)
  }

  return {
    toggleLegend,
    openLegend,
  }
}

// Accessibility helpers
export const getControlAccessibilityProps = (areControlsVisible: boolean) => ({
  controlsButton: {
    ariaLabel: areControlsVisible
      ? 'Hide navigation controls'
      : 'Show navigation controls',
    title: areControlsVisible
      ? 'Hide navigation controls'
      : 'Show navigation controls',
  },
  legendButton: {
    ariaLabel: 'Legend',
    title: 'Legend',
  },
  panButtons: {
    up: { ariaLabel: 'Pan Up' },
    down: { ariaLabel: 'Pan Down' },
    left: { ariaLabel: 'Pan Left' },
    right: { ariaLabel: 'Pan Right' },
  },
  zoomButtons: {
    in: { ariaLabel: 'Zoom In' },
    out: { ariaLabel: 'Zoom Out' },
  },
})
